import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { publicApi } from "../lib/api";
import {
  ArrowRight,
  Lightbulb,
  CheckCircle2,
  Sparkles,
  Inbox,
  User,
  MessagesSquare,
  CalendarClock,
} from "lucide-react";

const TABS = [
  { id: "all", label: "الكل" },
  { id: "approved", label: "المعتمدة" },
  { id: "implemented", label: "تم التنفيذ" },
];

const STATUS_META = {
  approved: {
    label: "معتمد",
    Icon: CheckCircle2,
    pill: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  },
  implemented: {
    label: "تم التنفيذ",
    Icon: Sparkles,
    pill: "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]",
  },
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const SuggestionCard = ({ item }) => {
  const meta = STATUS_META[item.status] || STATUS_META.approved;
  const author = item.name?.trim() || "زائر";
  return (
    <article
      data-testid={`public-suggestion-${item.id}`}
      className="rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-[#C9A96A]/30 p-4 sm:p-5 flex flex-col gap-3 transition-colors"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${meta.pill}`}
        >
          <meta.Icon className="w-3 h-3" />
          {meta.label}
        </span>
        <div className="flex items-center gap-1 text-white/40 text-[11px]">
          <CalendarClock className="w-3 h-3" />
          {formatDate(item.created_at)}
        </div>
      </div>

      <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap break-words">
        {item.content}
      </p>

      {item.admin_reply && (
        <div className="rounded-xl bg-[#C9A96A]/[0.05] border border-[#C9A96A]/25 p-3 flex gap-2">
          <MessagesSquare className="w-4 h-4 text-[#E5CB94] flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10px] font-bold text-[#E5CB94] mb-1">
              رد الإدارة
            </div>
            <div className="text-white/80 text-xs leading-relaxed whitespace-pre-wrap break-words">
              {item.admin_reply}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-white/50 text-[11px] pt-2 border-t border-white/5">
        <User className="w-3 h-3" />
        <span>{author}</span>
      </div>
    </article>
  );
};

const SuggestionsPage = () => {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ approved: 0, implemented: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    let ignore = false;
    setLoading(true);
    setError("");
    publicApi
      .get(`/suggestions/public?status=${tab}`)
      .then((res) => {
        if (ignore) return;
        setItems(res.data.items || []);
        setCounts(res.data.counts || { approved: 0, implemented: 0, total: 0 });
      })
      .catch(() => {
        if (ignore) return;
        setError("تعذّر تحميل الاقتراحات. حاول لاحقًا.");
      })
      .finally(() => !ignore && setLoading(false));
    return () => {
      ignore = true;
    };
  }, [tab]);

  const filteredCount = useMemo(() => {
    if (tab === "all") return counts.total;
    return counts[tab] || 0;
  }, [tab, counts]);

  return (
    <div className="App min-h-screen bg-[#0A1128] text-white" dir="rtl">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04] z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative z-10">
        <Header />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
          <Link
            to="/"
            data-testid="suggestions-back-link"
            className="inline-flex items-center gap-1.5 text-slate-300 hover:text-[#E5CB94] text-sm transition-colors"
          >
            <ArrowRight className="w-4 h-4" />
            <span>الرجوع للرئيسية</span>
          </Link>
        </div>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C9A96A]/[0.06] border border-[#C9A96A]/20 text-[#D4B98A] text-[11px] font-medium mb-3">
              <Lightbulb className="w-3.5 h-3.5" />
              <span>اقتراحات المجتمع</span>
            </div>
            <h1 className="text-white text-3xl sm:text-4xl font-black tracking-tight">
              الاقتراحات المنشورة
            </h1>
            <p className="text-white/50 text-sm sm:text-base mt-2 max-w-xl mx-auto">
              اقتراحات وملاحظات اعتمدها فريق Rush Store أو تم تنفيذها فعليًا.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-6 -mx-1 px-1">
            {TABS.map((t) => {
              const active = tab === t.id;
              const badgeCount =
                t.id === "all"
                  ? counts.total
                  : t.id === "approved"
                  ? counts.approved
                  : counts.implemented;
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`suggestions-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors border ${
                    active
                      ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]"
                      : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
                  }`}
                >
                  {t.label}
                  <span
                    className={`ms-1.5 px-1.5 py-0.5 rounded-md text-[10px] ${
                      active
                        ? "bg-[#0A1128]/50 text-[#E5CB94]"
                        : "bg-white/[0.04] text-slate-400"
                    }`}
                  >
                    {badgeCount}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Body */}
          {loading ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-slate-400 text-center py-16 text-sm">
              جارٍ التحميل...
            </div>
          ) : error ? (
            <div className="rounded-2xl bg-red-500/[0.06] border border-red-500/30 text-red-200 text-center py-10 text-sm">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="suggestions-empty"
              className="rounded-2xl bg-white/[0.02] border border-white/10 text-center py-16 px-4"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-3">
                <Inbox className="w-6 h-6 text-white/40" />
              </div>
              <div className="text-white font-bold text-sm">
                لا توجد اقتراحات منشورة حاليًا.
              </div>
              <div className="text-white/50 text-xs mt-1">
                كن أول من يشاركنا اقتراحه من الصفحة الرئيسية.
              </div>
            </div>
          ) : (
            <>
              <div className="text-white/50 text-xs mb-3 tabular-nums">
                {filteredCount} اقتراحًا
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {items.map((it) => (
                  <SuggestionCard key={it.id} item={it} />
                ))}
              </div>
            </>
          )}
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default SuggestionsPage;
